import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, FolderLock, Link2, Loader, Plus, ScrollText, Unlink, X } from 'lucide-react';
import { SectionHeader } from '../ui/SectionHeader';
import { SkeletonLoader } from '../SkeletonLoader';
import { LogbookEventList } from './LogbookEventList';
import { CaseChooser, caseChoiceIsUsable, type CaseChoice } from './CaseChooser';
import { PlayerProfile } from '../PlayerProfile';
import { useAuth } from '../../contexts/AuthContext';
import { useLogbookServers } from '../../hooks/useLogbookServers';
import { logbookApiService, LogbookApiError } from '../../services/logbookApi';
import { canProtectLogbook, canViewLogbook } from '../../utils/logbookPermissions';
import { formatCount, relativeTime } from '../../utils/logbook';
import { linkRecordToCase } from '../../utils/logbookCaseLink';
import { groupEvents } from '../../utils/logbookView';
import { formatLocalDateTime } from '../../utils/timestamps';
import { cn } from '../../utils/cn';
import type { LogbookCase, LogbookCaseDetail, LogbookCaseItemType, LogbookEvent } from '../../types/logbook';

interface LogbookCaseSectionProps {
  itemType: LogbookCaseItemType;
  /** A ban's numeric id is passed as its decimal string; everything else already has a text id. */
  itemId: string | number;
  /** Who the record is about. Floats their cases to the top, and names them on a newly opened case. */
  subjectUuid?: string;
  className?: string;
}

const STATUS_TONE: Record<LogbookCase['status'], string> = {
  open: 'badge-success',
  closed: 'badge-neutral',
  archived: 'badge-neutral',
};

/**
 * The Logbook evidence behind a note, warning or ban.
 *
 * Wherever a moderation record is read, the question that follows is what it was based on. Because a
 * case stores its own copy of each record, this section still answers months later: central
 * retention, a rollup or a world reset cannot empty it.
 *
 * It renders nothing at all when there is no case and the reader cannot make one, so an ordinary
 * note stays an ordinary note.
 */
export const LogbookCaseSection = ({ itemType, itemId, subjectUuid, className }: LogbookCaseSectionProps) => {
  const { identity } = useAuth();
  const canRead = canViewLogbook(identity);
  const canManage = canProtectLogbook(identity);
  const id = String(itemId);

  const [cases, setCases] = useState<LogbookCase[] | null>(null);
  const [linking, setLinking] = useState(false);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!canRead || !id) return;
    const controller = new AbortController();
    logbookApiService.casesForItem(itemType, id, controller.signal)
      .then((result) => setCases(result.cases ?? []))
      .catch(() => setCases([]));
    return () => controller.abort();
  }, [canRead, itemType, id, nonce]);

  if (!canRead) return null;
  if (cases === null) return <SkeletonLoader className={cn('h-20 w-full rounded-card', className)} />;
  if (cases.length === 0 && !canManage) return null;

  return (
    <div className={cn('glass-card p-4 md:p-5', className)}>
      <SectionHeader
        title="Logbook evidence"
        icon={FolderLock}
        count={cases.length || undefined}
        description={cases.length === 0 ? 'No case attached yet.' : undefined}
        actions={canManage && (
          <button type="button" className="glass-button-sm" onClick={() => setLinking(true)}>
            <Plus className="w-4 h-4" />
            Link a case
          </button>
        )}
      />

      {cases.length > 0 && (
        <div className="space-y-2">
          {cases.map((entry) => (
            <CaseCard
              key={entry.id}
              summary={entry}
              canManage={canManage}
              onUnlinked={() => setNonce((value) => value + 1)}
              itemType={itemType}
              itemId={id}
            />
          ))}
        </div>
      )}

      {linking && (
        <LinkCaseModal
          itemType={itemType}
          itemId={id}
          subjectUuid={subjectUuid}
          alreadyLinked={new Set(cases.map((entry) => entry.id))}
          onClose={() => setLinking(false)}
          onLinked={() => { setLinking(false); setNonce((value) => value + 1); }}
        />
      )}
    </div>
  );
};

interface CaseCardProps {
  summary: LogbookCase;
  canManage: boolean;
  itemType: LogbookCaseItemType;
  itemId: string;
  onUnlinked: () => void;
}

/** One attached case: its shape when collapsed, its actual evidence when opened. */
const CaseCard = ({ summary, canManage, itemType, itemId, onUnlinked }: CaseCardProps) => {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState<LogbookCaseDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const { byId: serversById } = useLogbookServers();

  // The records load on demand, so a note page with three attached cases costs one request, not four.
  useEffect(() => {
    if (!open || detail) return;
    const controller = new AbortController();
    setLoading(true);
    logbookApiService.getCase(summary.id, controller.signal)
      .then(setDetail)
      .catch(() => undefined)
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [open, detail, summary.id]);

  const runs = useMemo(() => {
    const events = (detail?.records ?? [])
      .map((record) => record.event)
      .filter((event): event is LogbookEvent => Boolean(event));
    return groupEvents(events, 300_000);
  }, [detail]);

  const unlink = useCallback(async () => {
    const link = detail?.links.find((entry) => entry.item_type === itemType && entry.item_id === itemId);
    if (!link) return;
    try {
      await logbookApiService.unlinkCase(summary.id, link.id);
      onUnlinked();
    } catch { /* The section reloads on the next open; a failed unlink is not worth an alert here. */ }
  }, [detail, itemType, itemId, summary.id, onUnlinked]);

  const window = summary.earliest_event_at && summary.latest_event_at
    ? summary.earliest_event_at === summary.latest_event_at
      ? formatLocalDateTime(new Date(summary.latest_event_at))
      : `${formatLocalDateTime(new Date(summary.earliest_event_at))} to ${formatLocalDateTime(new Date(summary.latest_event_at))}`
    : null;

  return (
    <div className="subcard overflow-hidden">
      <div className="flex items-start gap-3 p-3">
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
          className="flex-1 min-w-0 text-left"
        >
          <div className="flex flex-wrap items-center gap-2 min-w-0">
            <span className="badge badge-accent font-mono">{summary.reference}</span>
            <span className="text-sm text-white truncate">{summary.title}</span>
            <span className={cn('badge', STATUS_TONE[summary.status])}>{summary.status}</span>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-glass-500 tabular-nums">
            <span>{formatCount(summary.record_count)} {summary.record_count === 1 ? 'record' : 'records'}</span>
            {summary.subject_count > 0 && (
              <>
                <span aria-hidden>&middot;</span>
                <span>{formatCount(summary.subject_count)} named</span>
              </>
            )}
            {window && (
              <>
                <span aria-hidden>&middot;</span>
                <span>{window}</span>
              </>
            )}
            <span aria-hidden>&middot;</span>
            <span>updated {relativeTime(summary.updated_at)}</span>
          </div>
        </button>

        <div className="flex items-center gap-1 shrink-0">
          <Link to={`/logbook/cases/${summary.id}`} className="btn-icon-sm" title="Open case" aria-label="Open case">
            <Link2 className="w-3.5 h-3.5" />
          </Link>
          {canManage && detail && (
            <button type="button" onClick={unlink} className="btn-icon-sm" title="Unlink this case" aria-label="Unlink this case">
              <Unlink className="w-3.5 h-3.5" />
            </button>
          )}
          <button
            type="button"
            onClick={() => setOpen((value) => !value)}
            className="btn-icon-sm"
            title={open ? 'Hide evidence' : 'Show evidence'}
            aria-label={open ? 'Hide evidence' : 'Show evidence'}
          >
            <ChevronDown className={cn('w-3.5 h-3.5 transition-transform', open && 'rotate-180')} />
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-glass-200/10">
          {detail?.subjects.length ? (
            <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-glass-200/10">
              <span className="eyebrow">Named</span>
              {detail.subjects.map((subject) => (
                <span key={subject.id} className="chip gap-1.5" title={subject.role}>
                  <PlayerProfile uuid={subject.subject_uuid} inline showUuid={false} />
                  <span className="text-glass-500">{subject.role}</span>
                </span>
              ))}
            </div>
          ) : null}

          {loading && !detail ? (
            <div className="p-3 space-y-2">
              {Array.from({ length: 3 }, (_, index) => <SkeletonLoader key={index} className="h-10 w-full" />)}
            </div>
          ) : runs.length === 0 ? (
            <p className="px-3 py-4 text-sm text-glass-500 text-center">
              <ScrollText className="w-4 h-4 inline mr-1.5 -mt-0.5" />
              This case has no evidence attached yet.
            </p>
          ) : (
            <LogbookEventList runs={runs} serversById={serversById} />
          )}
        </div>
      )}
    </div>
  );
};

interface LinkCaseModalProps {
  itemType: LogbookCaseItemType;
  itemId: string;
  subjectUuid?: string;
  alreadyLinked: Set<string>;
  onClose: () => void;
  onLinked: () => void;
}

/**
 * Attaches a case to this note, warning or ban, opening one if the investigation has no case yet.
 *
 * Opening a case is offered here rather than only on the cases page, because the moment somebody
 * decides a ban belongs in a case is usually the moment the case first needs to exist.
 */
const LinkCaseModal = ({ itemType, itemId, subjectUuid, alreadyLinked, onClose, onLinked }: LinkCaseModalProps) => {
  const [choice, setChoice] = useState<CaseChoice | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKeyDown = (keyEvent: KeyboardEvent) => { if (keyEvent.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const link = async () => {
    if (!choice || !caseChoiceIsUsable(choice) || saving) return;
    setSaving(true);
    setError(null);
    try {
      await linkRecordToCase(choice, itemType, itemId, subjectUuid);
      onLinked();
    } catch (caught) {
      setError(caught instanceof LogbookApiError ? caught.message : 'That case could not be linked.');
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay z-[9999]">
      <div className="modal-panel max-w-lg max-md:max-w-[95vw]">
        <div className="modal-header">
          <div>
            <h2 className="modal-title">Link a case</h2>
            <p className="modal-subtitle">Attach an investigation case to this {itemType}, or open a new one.</p>
          </div>
          <button type="button" onClick={onClose} className="modal-close" title="Close" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="modal-body space-y-3">
          {error && <div className="alert alert-danger"><span>{error}</span></div>}

          <CaseChooser
            value={choice}
            onChange={setChoice}
            exclude={alreadyLinked}
            subject={subjectUuid}
            autoFocus
          />
        </div>

        <div className="modal-footer">
          <button type="button" className="glass-button-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="btn-primary" onClick={link} disabled={!caseChoiceIsUsable(choice) || saving}>
            {saving ? <Loader className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
            {choice?.mode === 'new' ? 'Open and link' : 'Link case'}
          </button>
        </div>
      </div>
    </div>
  );
};
